import React, { useEffect, useState, useMemo, useRef } from "react";
import { Button, Col, Row } from "react-bootstrap";
import SiteCard from "../sites/SiteCard";
import PlantedAcresCard from "./PlantedAcresCard";
import EmptyPlantedAcres from "./EmptyPlantedAcres";
import PlantedAcresForm from "./PlantedAcresForm";
import "styles/PlantedAcres.css";
import PlantedAcresFilter from "./PlantedAcresFilter";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedPlantedAcre } from "reduxStore/slices/plantedAcresSlice";
import PlantedAcresService from "services/PlantedAcresService";
import { setPlantedAcres } from "reduxStore/slices/plantedAcresSlice";
import {
  setSearch,
  setDebouncedSearch,
  setYear,
} from "reduxStore/slices/plantedAcresFilterSlice";
import {
  LABEL_SEARCH_PLANTED_ACRES,
  PLANTED_ACRES_FETCH_ERROR,
} from "configurations/MessageConstants";
import CardSkeleton from "components/common/CardSkeleton";
import { setEditedPlantedAcre } from "reduxStore/slices/plantedAcresSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { Form } from "react-bootstrap";
import { setRecipeUsages } from "reduxStore/slices/recipeUsageSlice";
import { MODE_TYPES } from "configurations/Constants";
import { debounce } from "lodash";
import CustomPagination from "components/common/CustomPagination";
import { DateHelper } from "utils/DateHelper";

const PlantedAcres = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const abortControllerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const selectedSite = useSelector((state) => state.site.selectedSite);
  const [showAddPlantedAcres, setShowAddPlantedAcres] = useState(false);
  const [showPlantedAcresFilter, setShowPlantedAcresFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginatedPlantedAcres, setPaginatedPlantedAcres] = useState([]);
  const plantedAcres = useSelector((state) => state.plantedAcres.plantedAcres);
  const isSharedViewOnly = selectedSite.isShared && !selectedSite.editor;
  const {
    year,
    selectedCrops,
    selectedSeeds,
    selectedStatus,
    selectedTillages,
    isFilterApplied,
    search,
    debouncedSearch,
  } = useSelector((state) => state.plantedAcresFilter);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((searchValue) => {
        dispatch(setDebouncedSearch(searchValue));
      }, 400),
    [dispatch]
  );

  useEffect(() => {
    debouncedSetSearch(search);
  }, [search, debouncedSetSearch]);

  useEffect(() => {
    if (location.state?.fromBack) {
      navigate(location.pathname, { state: { fromBack: false } });
      return;
    }
    getPlantedAcresBySiteId();
    dispatch(setSelectedPlantedAcre(null));
    dispatch(setRecipeUsages(null));
    setCurrentPage(1);
  }, [
    dispatch,
    navigate,
    debouncedSearch,
    year,
    selectedCrops,
    selectedSeeds,
    selectedStatus,
    selectedTillages,
  ]);

  const getPlantedAcresBySiteId = async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setIsLoading(true);
    let params = `year=${year}&search=${debouncedSearch}`;

    if (selectedCrops && selectedCrops.length > 0) {
      const cropNames = selectedCrops.map((crop) => crop.value).join(",");
      params += `&cropName=${cropNames}`;
    }

    if (selectedSeeds && selectedSeeds.length > 0) {
      const seedNames = selectedSeeds.map((seed) => seed.value).join(",");
      params += `&seedName=${seedNames}`;
    }

    if (selectedStatus && selectedStatus.length > 0) {
      const statusNames = selectedStatus
        .map((status) => status.value)
        .join(",");
      params += `&status=${statusNames}`;
    }

    if (selectedTillages && selectedTillages.length > 0) {
      const tillageNames = selectedTillages
        .map((tillage) => tillage.value)
        .join(",");
      params += `&tillageName=${tillageNames}`;
    }

    try {
      if (selectedSite && selectedSite.id) {
        const response = await PlantedAcresService.getPlantedAcresBySiteId(
          selectedSite.id,
          params,
          abortController.signal
        );
        if (!abortController.signal.aborted) {
          dispatch(setPlantedAcres(response.items));
        }
      }
    } catch (error) {
      if (!abortController.signal.aborted) {
        console.error(PLANTED_ACRES_FETCH_ERROR, error);
        dispatch(setPlantedAcres(null));
      }
    } finally {
      if (
        abortControllerRef.current === abortController &&
        !abortController.signal.aborted
      ) {
        setIsLoading(false);
      }
    }
  };
  const handleBackToSites = () => {
    navigate("/sites", { state: { fromBack: true } });
  };

  const handlePlantedAcreClick = (selectedPlantedAcre) => {
    dispatch(setSelectedPlantedAcre(selectedPlantedAcre));
    navigate(
      `/sites/${selectedSite.id}/planted-acres/${selectedPlantedAcre.id}`
    );
  };

  const handleFilterToggle = () => {
    setShowPlantedAcresFilter(!showPlantedAcresFilter);
  };

  const handleAddPlantedAcres = () => {
    setShowAddPlantedAcres(true);
    dispatch(setEditedPlantedAcre(null));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // If selectedSite is null, redirect to sites page
  if (!selectedSite) {
    return null;
  }

  return (
    <div>
      <div className="d-flex align-items-center gap-2">
        <Button
          variant="link"
          className="p-0 text-black"
          onClick={handleBackToSites}
          aria-label="Back to Sites"
        >
          <i className="fa fa-arrow-left" aria-hidden="true"></i>
        </Button>
        <div className="d-flex align-items-center gap-2">
          <h5 className="m-0">Site Details</h5>
          <Form.Group className="m-0">
            <Form.Select
              className="input-underline pb-0"
              aria-label="Select Year"
              value={year}
              onChange={(e) => dispatch(setYear(e.target.value))}
              size="sm"
            >
              {DateHelper.getYears().map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>

        <Button
          variant="outline-dark"
          size="sm"
          className={`${
            isFilterApplied ? "filter-icon-active" : "filter-icon"
          } pl-3 position-absolute end-0 rounded-0`}
          onClick={handleFilterToggle}
          aria-label="Planted Acres Filter"
          title="Planted Acres Filter"
        >
          <i className="fa fa-filter" aria-hidden="true"></i>
        </Button>
      </div>
      <div className="my-3 header-card-shadow rounded">
        <SiteCard
          type={
            selectedSite.isShared ? "title-shared-site-card" : "title-site-card"
          }
          site={selectedSite}
          onSiteClick={() => {}}
        />
      </div>
      <div className="mt-3 mb-2">
        <div className="d-flex flex-column flex-md-row justify-content-md-between gap-2">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mt-2 mb-0">List of Planted Acres</h6>
            {!isSharedViewOnly && (
              <div className="d-flex d-md-none">
                <Button
                  variant="outline-dark"
                  size="sm"
                  className="rounded-pill"
                  onClick={handleAddPlantedAcres}
                >
                  <i className="fa fa-circle-plus me-1"></i> Planted Acres
                </Button>
              </div>
            )}
          </div>
          <div className="d-none d-md-flex align-items-center gap-2">
            <div className="position-relative">
              <Form.Control
                type="text"
                placeholder={LABEL_SEARCH_PLANTED_ACRES}
                className="input-underline pe-5"
                style={{ minWidth: "300px" }}
                aria-label="Search"
                value={search}
                onChange={(e) => dispatch(setSearch(e.target.value))}
              />
              {search ? (
                <i
                  className="fa fa-times filter-clear-search"
                  onClick={() => dispatch(setSearch(""))}
                ></i>
              ) : (
                <i className="fa fa-search filter-search"></i>
              )}
            </div>
            {!isSharedViewOnly && (
              <Button
                variant="outline-dark"
                size="sm"
                className="rounded-pill"
                onClick={handleAddPlantedAcres}
              >
                <i className="fa fa-circle-plus me-1"></i> Planted Acres
              </Button>
            )}
          </div>
        </div>
        <div className="d-block d-md-none mt-2">
          <div className="position-relative">
            <Form.Control
              type="text"
              placeholder={LABEL_SEARCH_PLANTED_ACRES}
              className="input-underline pe-5"
              aria-label="Search"
              value={search}
              onChange={(e) => dispatch(setSearch(e.target.value))}
            />
            {search ? (
              <i
                className="fa fa-times filter-clear-search"
                onClick={() => dispatch(setSearch(""))}
              ></i>
            ) : (
              <i className="fa fa-search filter-search"></i>
            )}
          </div>
        </div>
      </div>
      {isLoading || !plantedAcres ? (
        <CardSkeleton numberOfCards={3} />
      ) : plantedAcres.length > 0 ? (
        <div>
          <Row className="g-3">
            {(paginatedPlantedAcres.length > 0
              ? paginatedPlantedAcres
              : plantedAcres
            ).map((plantedAcre) => (
              <Col key={plantedAcre.id} sm={12} md={6} lg={6} xl={4}>
                <PlantedAcresCard
                  type="planted-acres-card"
                  plantedAcre={plantedAcre}
                  handlePlantedAcreClick={() =>
                    handlePlantedAcreClick(plantedAcre)
                  }
                />
              </Col>
            ))}
          </Row>
          <CustomPagination
            currentPage={currentPage}
            totalPages={Math.ceil(plantedAcres.length / 9)}
            data={plantedAcres}
            itemsPerPage={9}
            onPageChange={handlePageChange}
            onDataChange={setPaginatedPlantedAcres}
          />
        </div>
      ) : (
        <div className="my-5">
          <EmptyPlantedAcres onAddPlantedAcres={handleAddPlantedAcres} />
        </div>
      )}
      <PlantedAcresForm
        mode={MODE_TYPES.ADD}
        show={showAddPlantedAcres}
        onClose={() => setShowAddPlantedAcres(false)}
      />
      <PlantedAcresFilter
        show={showPlantedAcresFilter}
        handleClose={() => setShowPlantedAcresFilter(false)}
      />
    </div>
  );
};

export default PlantedAcres;
